import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { Wrench, Fuel, AlertCircle } from "lucide-react";

export default function OperationsSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const aircraft = team?.aircraft || [];
  const minMaintenance = aircraft.reduce((sum, ac) => {
    const mins = { turboprop: 30000, used_narrowbody: 50000, new_narrowbody: 40000, widebody: 80000 };
    return sum + (mins[ac.type] || 50000);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Maintenance */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" /> {t("decisions.maintenanceLabel")}</CardTitle>
            <CardDescription>{t("decisions.maintenanceDesc", { amount: formatCurrency(minMaintenance), count: aircraft.length })}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <div className="flex justify-between mb-1"><Label className="text-xs">{t("decisions.maintenanceSpending")}</Label><span className="font-mono text-sky-600 font-semibold text-sm">{formatCurrency(decision.maintenance_spending || 0)}</span></div>
              <Slider value={[decision.maintenance_spending || 150000]} onValueChange={([v]) => updateDecision("maintenance_spending", v)} min={0} max={2000000} step={25000} className="mt-2" data-testid="maintenance-slider" />
              {(decision.maintenance_spending || 0) < minMaintenance && (
                <div className="flex items-center gap-1 text-rose-600 text-xs mt-1"><AlertCircle className="w-3 h-3" /> Below minimum! Reliability will drop.</div>
              )}
            </div>
            <div>
              <Label className="text-xs">{t("decisions.maintenanceStrategy")}</Label>
              <div className="space-y-2 mt-2">
                {[
                  { value: "reactive", label: "Reactive", desc: "Fix when broken (1.0x cost)", impact: "Low reliability" },
                  { value: "preventive", label: "Preventive", desc: "Scheduled maintenance (1.3x cost)", impact: "+reliability" },
                  { value: "predictive", label: "Predictive", desc: "AI-driven predictive (1.6x cost)", impact: "Best reliability" },
                ].map(opt => (
                  <button key={opt.value} onClick={() => updateDecision("maintenance_strategy", opt.value)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${decision.maintenance_strategy === opt.value ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`maint-${opt.value}`}>
                    <div className="flex justify-between"><span className="font-medium text-sm">{opt.label}</span><span className="text-xs text-slate-500">{opt.impact}</span></div>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operations */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.operationsService")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <Label className="text-xs">{t("decisions.onTimeTarget")}</Label>
              <Select value={decision.ontime_target || "medium"} onValueChange={v => updateDecision("ontime_target", v)}>
                <SelectTrigger className="mt-1" data-testid="ontime-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("decisions.cateringNone").replace(/\$0\/pax.*/, "$0")} (-{t("decisions.qualityRating")})</SelectItem>
                  <SelectItem value="medium">{t("decisions.standard")} ($50K)</SelectItem>
                  <SelectItem value="high">High ($150K, +{t("decisions.qualityRating")})</SelectItem>
                  <SelectItem value="premium">{t("decisions.premium")} ($300K, +4 {t("decisions.qualityRating")})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex justify-between mb-1"><Label className="text-xs">{t("decisions.fuelHedging")}</Label><span className="font-mono text-xs">{decision.fuel_hedging || 0}%</span></div>
              <Slider value={[decision.fuel_hedging || 0]} onValueChange={([v]) => updateDecision("fuel_hedging", v)} min={0} max={100} step={10} className="mt-2" data-testid="fuel-hedging-slider" />
              <p className="text-[10px] text-slate-400 mt-1">Hedging costs 5% premium but protects against fuel spikes</p>
            </div>

            <div>
              <Label className="text-xs">{t("decisions.groundHandling")}</Label>
              <Select value={decision.ground_handling || "outsourced"} onValueChange={v => updateDecision("ground_handling", v)}>
                <SelectTrigger className="mt-1" data-testid="ground-handling-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_house">{t("decisions.groundInHouse")}</SelectItem>
                  <SelectItem value="outsourced">{t("decisions.groundOutsourced")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">{t("decisions.cateringLevel")}</Label>
              <Select value={decision.catering_level || "basic"} onValueChange={v => updateDecision("catering_level", v)}>
                <SelectTrigger className="mt-1" data-testid="catering-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("decisions.cateringNone")}</SelectItem>
                  <SelectItem value="basic">{t("decisions.cateringBasic")}</SelectItem>
                  <SelectItem value="standard">{t("decisions.cateringStandard")}</SelectItem>
                  <SelectItem value="premium">{t("decisions.cateringPremium")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
